import React, { useState } from 'react';
import { Button, CircularProgress, Alert, Snackbar } from '@mui/material';
import { Save } from '@mui/icons-material';
import type { AssetVersionedView } from '@trackly/shared';
import { useAppSelector } from '../../../../../app/hooks';
import { SaveNotice, useSaveAsset } from '../../../hooks/useSaveAsset';
import { AssetConflictDialog } from './AssetConflictDialog';

interface SaveAssetButtonProps {
  label?: string;
  successMessage?: string;
}

const REQUIRED_FIELDS: ['name' | 'assetTag' | 'category' | 'center' | 'status', string][] = [
  ['name', 'Name'],
  ['assetTag', 'Asset Tag'],
  ['category', 'Category'],
  ['center', 'Center'],
  ['status', 'Status'],
];

function requiredFieldsNotice(asset: AssetVersionedView): SaveNotice | null {
  const missingFields = REQUIRED_FIELDS.filter(([key]) => !asset[key]?.trim()).map(([, label]) => label);
  if (missingFields.length === 0) return null;

  return {
    severity: 'error',
    message: `Please complete the following required fields: ${missingFields.join(', ')}`,
    persistent: false,
  };
}

const NoticeSnackbar: React.FC<{ notice: SaveNotice | null; onClose: () => void }> = ({ notice, onClose }) => {
  if (!notice) return null;

  const isError = notice.severity === 'error';
  return (
    <Snackbar
      open
      autoHideDuration={notice.persistent ? null : 6000}
      onClose={onClose}
      anchorOrigin={{ vertical: isError ? 'top' : 'bottom', horizontal: 'center' }}
    >
      <Alert onClose={onClose} severity={notice.severity} variant={isError ? 'standard' : 'filled'} sx={{ width: '100%' }}>
        {notice.message}
      </Alert>
    </Snackbar>
  );
};

const SaveActionButton: React.FC<{ label: string; isSaving: boolean; disabled: boolean; onClick: () => void }> = ({
  label,
  isSaving,
  disabled,
  onClick,
}) => (
  <Button
    variant="contained"
    onClick={onClick}
    disabled={disabled}
    startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : <Save />}
    sx={{
      backgroundColor: '#162346',
      color: 'white',
      fontWeight: 600,
      '&:hover': {
        backgroundColor: '#1a2a52',
      },
      '&:disabled': {
        backgroundColor: '#64748b',
        color: '#ffffff',
      },
    }}
  >
    {isSaving ? 'Saving...' : label}
  </Button>
);

const SaveAssetButton: React.FC<SaveAssetButtonProps> = ({
  label = 'Save Asset',
  successMessage = 'Asset saved successfully!',
}) => {
  const [validationNotice, setValidationNotice] = useState<SaveNotice | null>(null);
  const { currentAsset, isEdited } = useAppSelector(state => state.asset);
  const { isSaving, notice, clearNotice, conflict, save, setChoice, confirmConflict, cancelConflict } =
    useSaveAsset(successMessage);

  const handleSave = async () => {
    if (!currentAsset || !isEdited) return;

    const invalid = requiredFieldsNotice(currentAsset);
    setValidationNotice(invalid);
    if (invalid) {
      clearNotice();
      return;
    }

    await save();
  };

  return (
    <>
      <SaveActionButton label={label} isSaving={isSaving} disabled={!isEdited || isSaving} onClick={handleSave} />

      <NoticeSnackbar notice={validationNotice} onClose={() => setValidationNotice(null)} />
      <NoticeSnackbar notice={notice} onClose={clearNotice} />

      {conflict && (
        <AssetConflictDialog
          conflict={conflict}
          onChoiceChange={setChoice}
          onCancel={cancelConflict}
          onConfirm={confirmConflict}
          isResolving={isSaving}
        />
      )}
    </>
  );
};

export default SaveAssetButton;